import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';

const InvoiceForm = ({ onClose, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [publishers, setPublishers] = useState([]);
    
    const [formData, setFormData] = useState({
        userId: '',
        invoiceDate: '',
        invoiceAmount: '',
        ivtDeduction: '',
        monitizeMediaPercentage: '',
        finalRevenue: '',
        status: 'Pending'
    });

    useEffect(() => {
        fetchPublishers();
    }, []);



    const fetchPublishers = async () => {
        try {
            const response = await PublisherApiService.getAllApproved();
            console.log("response publisher", response);
            setPublishers(response.data || []);
        } catch (error) {
            console.error('Failed to fetch publishers', error);
            toast.error('Failed to load publishers');
        }
    };

    const calculateFinalRevenue = (amount, ivt, mmPercent) => {
        const invoiceAmt = parseFloat(amount) || 0;
        const ivtDed = parseFloat(ivt) || 0;
        const mmPercentage = parseFloat(mmPercent) || 0;
        
        const afterIvt = invoiceAmt - ivtDed;
        const mmDeduction = (afterIvt * mmPercentage) / 100;
        const final = afterIvt - mmDeduction;
        
        return final.toFixed(2);
    };

    const getDateRangeFromInvoiceDate = (invoiceDate) => {
        if (!invoiceDate) return { fromDate: '', toDate: '' };
        
        const date = new Date(invoiceDate);
        const year = date.getFullYear();
        const month = date.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const fromDate = firstDay.toISOString().split('T')[0];
        const toDate = lastDay.toISOString().split('T')[0];
        
        return { fromDate, toDate };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedData = { ...formData, [name]: value };

        if (['invoiceAmount', 'ivtDeduction', 'monitizeMediaPercentage'].includes(name)) {
            updatedData.finalRevenue = calculateFinalRevenue(
                updatedData.invoiceAmount,
                updatedData.ivtDeduction,
                updatedData.monitizeMediaPercentage
            );
        }

        setFormData(updatedData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.userId || !formData.invoiceAmount || !formData.invoiceDate) {
            toast.error('Please fill all required fields');
            return;
        }

        setLoading(true);
        try {
            const { fromDate, toDate } = getDateRangeFromInvoiceDate(formData.invoiceDate);
            
            await onSave({
                ...formData,
                fromDate: fromDate,
                toDate: toDate,
                invoiceAmount: parseFloat(formData.invoiceAmount),
                ivtDeduction: parseFloat(formData.ivtDeduction) || 0,
                monitizeMediaPercentage: parseFloat(formData.monitizeMediaPercentage) || 0,
                finalRevenue: parseFloat(formData.finalRevenue) || 0
            });
            toast.success('Invoice created successfully');
            onClose();
        } catch (error) {
            console.error('Failed to create invoice', error);
            toast.error('Failed to create invoice');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                    <h3 className="text-xl font-semibold text-gray-800">
                        Create New Invoice
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-red-600 transition">
                        <X size={22} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Publisher <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="userId"
                                value={formData.userId}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                required
                            >
                                <option value="">Select Publisher</option>
                                {publishers.map(pub => (
                                    <option key={pub.id} value={pub.id}>
                                        {pub.name} ({pub.email})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="invoiceDate"
                                value={formData.invoiceDate}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Invoice period will be calculated from this date's month
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Amount ($) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="invoiceAmount"
                                value={formData.invoiceAmount}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                placeholder="Enter invoice amount"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Deduction/Tech Cost ($)
                            </label>
                            <input
                                type="number"
                                name="ivtDeduction"
                                value={formData.ivtDeduction}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                MonitizeMedia Share (%)
                            </label>
                            <input
                                type="number"
                                name="monitizeMediaPercentage"
                                value={formData.monitizeMediaPercentage}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder="0.00"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Final Revenue ($)
                            </label>
                            <input
                                type="number"
                                name="finalRevenue"
                                value={formData.finalRevenue}
                                readOnly
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="Pending">Pending</option>
                                <option value="Sent">Sent</option>
                                <option value="Paid">Paid</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Create
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InvoiceForm;
